import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { AuthService, UserData, RegisterRequest } from '@/services/auth.service';
import { getStoredCartId, clearStoredCartId } from '@/contexts/CartContext';

interface AuthContextType {
  isAuthenticated: boolean;
  user: UserData | null;
  token: string | null;
  isLoading: boolean;
  isInitializing: boolean;
  error: string | null;
  requestOTP: (phone: string) => Promise<void>;
  verifyOTP: (phone: string, otp: string) => Promise<void>;
  register: (data: RegisterRequest) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const TOKEN_KEY = 'indispare_token';
const USER_KEY = 'indispare_user';

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<UserData | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load auth state from localStorage on mount
  useEffect(() => {
    const loadAuthState = () => {
      const storedToken = localStorage.getItem(TOKEN_KEY);
      const storedUser = localStorage.getItem(USER_KEY);
      console.log('Loading auth state from localStorage:', { storedToken: !!storedToken, storedUser: !!storedUser });

      if (storedToken && storedUser) {
        try {
          const parsedUser = JSON.parse(storedUser);
          setToken(storedToken);
          setUser(parsedUser);
          setIsAuthenticated(true);
          console.log('Auth state loaded successfully:', { user: parsedUser, isAuthenticated: true });
        } catch (error) {
          console.error('Error parsing stored auth data:', error);
          // Invalid stored data, clear it
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem(USER_KEY);
        }
      } else {
        console.log('No stored auth data found');
      }
      
      // Mark initialization as complete
      setIsInitializing(false);
    };

    loadAuthState();
  }, []);

  const requestOTP = async (phone: string) => {
    try {
      setIsLoading(true);
      setError(null);
      await AuthService.requestOTP(phone);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to request OTP');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const verifyOTP = async (phone: string, otp: string) => {
    try {
      setIsLoading(true);
      setError(null);
      console.log('Verifying OTP for phone:', phone);
      
      // Get cart ID from localStorage if exists
      const cartId = getStoredCartId();
      console.log('Cart ID from localStorage:', cartId);
      
      const response = await AuthService.verifyOTP(phone, otp, cartId || undefined);
      console.log('OTP verification response:', response);

      if (response.token && response.data) {
        // Store token and user data
        console.log('Storing token and user data');
        localStorage.setItem(TOKEN_KEY, response.token);
        localStorage.setItem(USER_KEY, JSON.stringify(response.data));

        setToken(response.token);
        setUser(response.data);
        setIsAuthenticated(true);
        console.log('Authentication state updated:', { token: response.token, user: response.data, isAuthenticated: true });
        
        // Clear cart ID from localStorage after successful login (cart is now merged with user account)
        if (cartId) {
          clearStoredCartId();
          console.log('Cleared cart ID from localStorage after login');
        }
      } else {
        console.error('Invalid response: missing token or user data', response);
        throw new Error('Invalid response: missing token or user data');
      }
    } catch (err) {
      console.error('OTP verification failed:', err);
      setError(err instanceof Error ? err.message : 'Failed to verify OTP');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (data: RegisterRequest) => {
    try {
      setIsLoading(true);
      setError(null);
      console.log('Registering new user');
      const response = await AuthService.register(data);
      console.log('Registration response:', response);

      // Check for validation errors (400 response)
      if (response.errors) {
        console.error('Registration validation errors:', response.errors);
        // Throw the errors object so it can be caught and displayed in the form
        throw { errors: response.errors, message: response.message || 'Validation failed' };
      }

      if (response.token && response.data) {
        // Auto-login: Store token and user data
        console.log('Registration successful, storing token and user data');
        localStorage.setItem(TOKEN_KEY, response.token);
        localStorage.setItem(USER_KEY, JSON.stringify(response.data));

        setToken(response.token);
        setUser(response.data);
        setIsAuthenticated(true);
        console.log('Authentication state updated after registration:', { token: response.token, user: response.data, isAuthenticated: true });
      } else {
        console.error('Invalid response: missing token or user data', response);
        throw new Error('Invalid response: missing token or user data');
      }
    } catch (err: any) {
      console.error('Registration failed:', err);
      // If it has errors property, it's a validation error, rethrow it as is
      if (err.errors) {
        throw err;
      }
      // Otherwise, set generic error
      setError(err instanceof Error ? err.message : 'Failed to register');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    // Clear all localStorage
    console.log('Logging out - clearing all localStorage');
    localStorage.clear();

    // Clear state
    setIsAuthenticated(false);
    setUser(null);
    setToken(null);
    setError(null);

    // Reload home page
    window.location.href = '/';
  };

  return (
    <AuthContext.Provider value={{
      isAuthenticated,
      user,
      token,
      isLoading,
      isInitializing,
      error,
      requestOTP,
      verifyOTP,
      register,
      logout
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
